import { Center, Flex, Input, Spinner, Text } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { AutoUpdatingRelativeTime } from "Components/comment/AutoUpdatingRelativeTime"
import { Avatar, IconButton } from "DesignSystem/components"
import { SendIcon } from "Icons/SendIcon"
import { useTranslate } from "Shared/hooks/translations/useTranslate"
import { useMaybeCurrentUser } from "UsabilityHub/hooks/useCurrentAccount"
import React, { useEffect, useRef, useState } from "react"
import { useUsabilityhubContext } from "~/api/generated/usabilityhub-context"
import { ChatMessage } from "~/api/generated/usabilityhubSchemas"

type Props = {
  messages: ChatMessage[]
  sendMessage: (text: string) => void
  onRead?: () => void
  emptyMessage: string
  helpMessage?: string
  perspective: "panelist" | "researcher"
  isLoading: boolean
  isReadOnly: boolean
}

export const ChatInterface: React.FC<Props> = ({
  messages,
  sendMessage,
  onRead,
  emptyMessage,
  helpMessage,
  perspective,
  isLoading,
  isReadOnly,
}) => {
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const { queryKeyFn } = useUsabilityhubContext()
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const [inputValue, setInputValue] = useState("")

  const unreadChatQueryKey = queryKeyFn({
    path: "/api/moderated_study_chat_messages/unread_chats",
    operationId: "listUnreadModeratedStudyChats",
    variables: {},
  })

  // Mark the chat as read if the channel supports it
  const markAsRead = () => {
    if (onRead) {
      onRead()

      // Invalidate the unread chats query
      queryClient.invalidateQueries({ queryKey: unreadChatQueryKey })
    }
  }
  // When a new message comes in...
  useEffect(() => {
    markAsRead()

    // Scroll chat to bottom
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [messages])

  return (
    <Flex
      direction="column"
      gap={4}
      bg="ds.background.neutral.resting"
      rounded="8"
      mt={6}
    >
      <Flex
        ref={chatContainerRef}
        direction="column"
        gap={4}
        h="400px"
        overflowY="auto"
        px={3}
        pt={3}
        pb={isReadOnly ? 3 : 0}
      >
        {messages.length === 0 ? (
          <Center flexGrow={1} mt={4}>
            {isLoading ? (
              <Spinner color="ds.text.subtle" />
            ) : (
              <Text textStyle="ds.paragraph.secondary" color="ds.text.subtle">
                {emptyMessage}
              </Text>
            )}
          </Center>
        ) : (
          messages.map((message, index) => (
            <IndividualMessage
              key={message.id}
              perspective={perspective}
              message={message}
              firstMessage={index === 0}
            />
          ))
        )}
      </Flex>

      {!isReadOnly && (
        <form
          onSubmit={(e) => {
            e.preventDefault()

            if (isLoading) return

            if (inputValue !== "") {
              sendMessage(inputValue)
              setInputValue("")
            }
          }}
        >
          <Flex w="full" gap={2} px={3} pb={helpMessage ? 0 : 3}>
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={markAsRead}
              autoFocus
              w="full"
              placeholder={translate("interview.chat.placeholder")}
            />
            <IconButton
              isDisabled={isLoading || inputValue === ""}
              size="emphasized"
              type="submit"
              icon={<SendIcon />}
              aria-label="Send message"
            />
          </Flex>
        </form>
      )}

      {helpMessage && (
        <Text
          align="center"
          textStyle="ds.paragraph.secondary"
          color="ds.text.subtle"
          mb={3}
        >
          {helpMessage}
        </Text>
      )}
    </Flex>
  )
}

const IndividualMessage: React.FC<{
  perspective: "panelist" | "researcher"
  message: ChatMessage
  firstMessage: boolean
}> = ({ perspective, message, firstMessage }) => {
  const currentUser = useMaybeCurrentUser()
  const currentUserId = currentUser?.id ?? null
  const myMessage =
    perspective === message.source && currentUserId === message.user_id

  return (
    <Flex
      w="full"
      direction={message.source === perspective ? "row-reverse" : "row"}
      justify="flex-start"
      align="flex-end"
      gap={2}
      mt={firstMessage ? "auto" : 0}
    >
      <Avatar
        size="small"
        appearance="profile"
        profileImageSrc={message.avatar_url ?? undefined}
        name={message.display_name ?? ""}
      />

      <Flex
        maxW="400px"
        direction="column"
        gap={2}
        bg={
          myMessage
            ? "ds.background.brand.subtle.resting"
            : "ds.background.neutral.resting"
        }
        p={2}
        borderRadius={8}
      >
        <Text textStyle="ds.paragraph.primary" color="ds.text.default">
          {message.text}
        </Text>

        <Text
          textStyle="ds.paragraph.secondary"
          color={myMessage ? "ds.text.subtle" : "ds.text.subtlest"}
          whiteSpace="nowrap"
        >
          {message.source !== perspective ? `${message.display_name},` : null}{" "}
          <AutoUpdatingRelativeTime as="span" date={message.sent_at} />
        </Text>
      </Flex>
    </Flex>
  )
}
